/**
 * Test Planning Modal
 * 
 * A modal component that helps users calculate sample size and test duration
 * for their A/B experiments. Integrates into the experiment creation wizard.
 */

import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  calculateSampleSize,
  calculateTestDuration,
  formatDuration,
  formatNumber,
  type SampleSizeResult,
  type TestDurationResult,
  type TrafficAssessment,
} from '@/lib/utils/abTestCalculations'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { Link, useParams } from 'react-router-dom'
import { HugeiconsIcon } from "@hugeicons/react"
import { CalculatorIcon, Clock01Icon, InformationCircleIcon } from "@hugeicons/core-free-icons"

// ============================================================================
// Types
// ============================================================================

interface TestPlanningModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  defaultValues?: {
    dailyVisitors?: number
    variantCount?: number
    trafficDistribution?: number[]
  }
}

interface FormData {
  dailyVisitors: string
  baseline: string
  mde: string
  significance: string
}

// ============================================================================
// Helpers
// ============================================================================

function severityColor(severity: TrafficAssessment['severity']): string {
  switch (severity) {
    case 'good': return 'border-success/30 bg-success-tint dark:border-success/30 dark:bg-success-tint'
    case 'warning': return 'border-warning/30 bg-warning-tint dark:border-warning/30 dark:bg-warning-tint'
    case 'critical': return 'border-[#FECDCA] bg-danger-tint dark:border-danger/40 dark:bg-danger-tint'
  }
}

function severityIcon(severity: TrafficAssessment['severity']): string {
  switch (severity) {
    case 'good': return '\u2705'
    case 'warning': return '\u26A0\uFE0F'
    case 'critical': return '\u26D4'
  }
}

// ============================================================================
// Full Planner Link Helper
// ============================================================================

function FullPlannerLink({ onOpenChange }: { onOpenChange: (open: boolean) => void }) {
  const params = useParams()
  const basePath = params.workspaceId
    ? `/workspace/${params.workspaceId}`
    : params.propertySlugId
    ? `/p/${params.propertySlugId}`
    : ''

  return (
    <div className="pt-2 border-t border-border/30 text-center">
      <Link
        to={`${basePath}/test-planner`}
        onClick={() => onOpenChange(false)}
        className="text-xs text-primary hover:text-primary/80 font-medium transition-colors"
      >
        Open full Test Planner &rarr;
      </Link>
    </div>
  )
}

// ============================================================================
// Component
// ============================================================================

export function TestPlanningModal({
  open,
  onOpenChange,
  defaultValues,
}: TestPlanningModalProps) {
  const [activeTab, setActiveTab] = useState<'duration' | 'sample-size'>('duration')
  const [formData, setFormData] = useState<FormData>({
    dailyVisitors: defaultValues?.dailyVisitors?.toString() || '',
    baseline: '',
    mde: '20',
    significance: '95',
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [result, setResult] = useState<TestDurationResult | SampleSizeResult | null>(null)

  // Update daily visitors if default changes
  useEffect(() => {
    if (defaultValues?.dailyVisitors && !formData.dailyVisitors) {
      setFormData(prev => ({
        ...prev,
        dailyVisitors: defaultValues.dailyVisitors!.toString(),
      }))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValues?.dailyVisitors])

  // Reset result when form changes
  useEffect(() => {
    setResult(null)
  }, [formData, activeTab])

  const variantCount = defaultValues?.variantCount || 2

  // Validation
  function validateForm(): boolean {
    const newErrors: Record<string, string> = {}

    if (activeTab === 'duration') {
      if (!formData.dailyVisitors) {
        newErrors.dailyVisitors = 'Required'
      } else if (parseFloat(formData.dailyVisitors) < 1) {
        newErrors.dailyVisitors = 'Must be at least 1'
      }
    }

    if (!formData.baseline) {
      newErrors.baseline = 'Required'
    } else if (parseFloat(formData.baseline) <= 0 || parseFloat(formData.baseline) >= 100) {
      newErrors.baseline = 'Must be 0-100'
    }

    if (!formData.mde) {
      newErrors.mde = 'Required'
    } else if (parseFloat(formData.mde) <= 0 || parseFloat(formData.mde) >= 100) {
      newErrors.mde = 'Must be 0-100'
    }

    if (!formData.significance) {
      newErrors.significance = 'Required'
    } else if (parseFloat(formData.significance) < 90 || parseFloat(formData.significance) > 99) {
      newErrors.significance = 'Must be 90-99'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  function handleCalculate() {
    if (!validateForm()) return

    if (activeTab === 'duration') {
      const durationResult = calculateTestDuration({
        dailyVisitors: parseFloat(formData.dailyVisitors),
        baseline: parseFloat(formData.baseline),
        mde: parseFloat(formData.mde),
        significance: parseFloat(formData.significance),
        variants: variantCount,
      })
      setResult(durationResult)
    } else {
      const sizeResult = calculateSampleSize({
        baseline: parseFloat(formData.baseline),
        mde: parseFloat(formData.mde),
        significance: parseFloat(formData.significance),
      })
      setResult(sizeResult)
    }
  }

  function handleInputChange(field: keyof FormData, value: string) {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }
  }

  // Type guard for duration result
  const isDurationResult = (r: TestDurationResult | SampleSizeResult | null): r is TestDurationResult => {
    return r !== null && 'days' in r
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">Test planning calculator</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            Calculate how much traffic you need and how long to run your test.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'duration' | 'sample-size')}>
          <TabsList className="grid w-full grid-cols-2 mb-4">
            <TabsTrigger value="duration" className="text-xs">
              <HugeiconsIcon icon={Clock01Icon} size={14} className="mr-1.5" />
              Test duration
            </TabsTrigger>
            <TabsTrigger value="sample-size" className="text-xs">
              <HugeiconsIcon icon={CalculatorIcon} size={14} className="mr-1.5" />
              Sample size
            </TabsTrigger>
          </TabsList>

          <div className="space-y-4">
            {/* Daily Visitors - only for duration tab */}
            {activeTab === 'duration' && (
              <div className="space-y-1.5">
                <div className="flex items-center gap-1.5">
                  <Label htmlFor="modal-dailyVisitors" className="text-xs font-medium">
                    Average daily visitors
                  </Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HugeiconsIcon icon={InformationCircleIcon} size={14} className="text-muted-foreground/60" />
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-[200px] text-xs">
                        Total unique visitors to the pages in your test per day
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  {defaultValues?.dailyVisitors && (
                    <span className="text-[11px] text-muted-foreground ml-auto">
                      Auto-filled from analytics
                    </span>
                  )}
                </div>
                <Input
                  id="modal-dailyVisitors"
                  type="number"
                  min="1"
                  placeholder="e.g., 500"
                  value={formData.dailyVisitors}
                  onChange={(e) => handleInputChange('dailyVisitors', e.target.value)}
                  className={cn(
                    "h-9 text-sm",
                    errors.dailyVisitors && "border-destructive"
                  )}
                />
                {errors.dailyVisitors && (
                  <p className="text-[11px] text-destructive">{errors.dailyVisitors}</p>
                )}
              </div>
            )}

            {/* Baseline Conversion Rate */}
            <div className="space-y-1.5">
              <div className="flex items-center gap-1.5">
                <Label htmlFor="modal-baseline" className="text-xs font-medium">
                  Baseline Conversion Rate (%)
                </Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <HugeiconsIcon icon={InformationCircleIcon} size={14} className="text-muted-foreground/60" />
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-[200px] text-xs">
                      Your current conversion rate. If you don't know, estimate based on industry averages (e.g., 2-5% for e-commerce)
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <Input
                id="modal-baseline"
                type="number"
                min="0.1"
                max="99"
                step="0.1"
                placeholder="e.g., 3.5"
                value={formData.baseline}
                onChange={(e) => handleInputChange('baseline', e.target.value)}
                className={cn(
                  "h-9 text-sm",
                  errors.baseline && "border-destructive"
                )}
              />
              {errors.baseline && (
                <p className="text-[11px] text-destructive">{errors.baseline}</p>
              )}
            </div>

            {/* Two columns for MDE and Significance */}
            <div className="grid grid-cols-2 gap-3">
              {/* Minimum Detectable Effect */}
              <div className="space-y-1.5">
                <div className="flex items-center gap-1.5">
                  <Label htmlFor="modal-mde" className="text-xs font-medium">
                    Min. Detectable Effect (%)
                  </Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HugeiconsIcon icon={InformationCircleIcon} size={14} className="text-muted-foreground/60" />
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-[200px] text-xs">
                        The smallest relative improvement you want to detect. 20% is a good starting point.
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Input
                  id="modal-mde"
                  type="number"
                  min="1"
                  max="100"
                  placeholder="20"
                  value={formData.mde}
                  onChange={(e) => handleInputChange('mde', e.target.value)}
                  className={cn(
                    "h-9 text-sm",
                    errors.mde && "border-destructive"
                  )}
                />
                {errors.mde && (
                  <p className="text-[11px] text-destructive">{errors.mde}</p>
                )}
              </div>

              {/* Statistical Significance */}
              <div className="space-y-1.5">
                <div className="flex items-center gap-1.5">
                  <Label className="text-xs font-medium">
                    Significance (%)
                  </Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HugeiconsIcon icon={InformationCircleIcon} size={14} className="text-muted-foreground/60" />
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-[200px] text-xs">
                        Confidence level. 95% is the industry standard.
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Select
                  value={formData.significance}
                  onValueChange={(v) => handleInputChange('significance', v)}
                >
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue placeholder="95%" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="90">90%</SelectItem>
                    <SelectItem value="95">95%</SelectItem>
                    <SelectItem value="99">99%</SelectItem>
                  </SelectContent>
                </Select>
                {errors.significance && (
                  <p className="text-[11px] text-destructive">{errors.significance}</p>
                )}
              </div>
            </div>

            {/* Variant count info */}
            {variantCount > 2 && (
              <p className="text-[11px] text-muted-foreground">
                Calculating for {variantCount} variants based on your configuration.
              </p>
            )}

            {/* Calculate Button */}
            <Button onClick={handleCalculate} className="w-full">
              {activeTab === 'duration' ? (
                <>
                  <HugeiconsIcon icon={Clock01Icon} size={16} className="mr-2" />
                  Calculate duration
                </>
              ) : (
                <>
                  <HugeiconsIcon icon={CalculatorIcon} size={16} className="mr-2" />
                  Calculate sample size
                </>
              )}
            </Button>

            {/* Results */}
            {result && (
              <div className="mt-4 p-4 rounded-lg bg-muted/50 border border-border/30 space-y-3">
                {isDurationResult(result) ? (
                  <>
                    <div className="flex items-baseline justify-between">
                      <span className="text-sm text-muted-foreground">Recommended duration</span>
                      <span className="text-xl font-semibold text-primary">
                        {formatDuration(result.days)}
                      </span>
                    </div>
                    <div className="flex items-baseline justify-between">
                      <span className="text-xs text-muted-foreground">Sample size per variant</span>
                      <span className="text-sm font-medium">
                        {formatNumber(result.sampleSizePerVariant)} visitors
                      </span>
                    </div>
                    <div className="flex items-baseline justify-between">
                      <span className="text-xs text-muted-foreground">Total visitors needed</span>
                      <span className="text-sm font-medium">
                        {formatNumber(result.totalSampleSize)} visitors
                      </span>
                    </div>
                    <div className="flex items-baseline justify-between">
                      <span className="text-xs text-muted-foreground">Review results on</span>
                      <span className="text-sm font-medium">
                        {result.reviewDate.toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </span>
                    </div>

                    {/* Traffic assessment warning */}
                    {result.trafficAssessment.severity !== 'good' && (
                      <div className={`mt-2 p-2.5 rounded-md border text-xs ${severityColor(result.trafficAssessment.severity)}`}>
                        <span className="mr-1">{severityIcon(result.trafficAssessment.severity)}</span>
                        {result.trafficAssessment.message}
                      </div>
                    )}

                    <p className="text-[11px] text-muted-foreground pt-2 border-t border-border/30">
                      Run your test for at least {formatDuration(result.days)} to collect enough data for reliable results.
                      Results are rounded to full weeks to account for day-of-week traffic variations.
                    </p>
                  </>
                ) : (
                  <>
                    <div className="flex items-baseline justify-between">
                      <span className="text-sm text-muted-foreground">Sample size per variant</span>
                      <span className="text-xl font-semibold text-primary">
                        {formatNumber(result.sampleSizePerVariant)}
                      </span>
                    </div>
                    <div className="flex items-baseline justify-between">
                      <span className="text-xs text-muted-foreground">Total visitors needed</span>
                      <span className="text-sm font-medium">
                        {formatNumber(result.totalSampleSize)} visitors
                      </span>
                    </div>
                    <p className="text-[11px] text-muted-foreground pt-2 border-t border-border/30">
                      You need {formatNumber(result.sampleSizePerVariant)} visitors per variant to detect a {formData.mde}% improvement with {formData.significance}% confidence.
                    </p>
                  </>
                )}
              </div>
            )}

            {/* Tips */}
            <div className="text-[11px] text-muted-foreground space-y-1 pt-2">
              <p>&#8226; Smaller improvements require larger sample sizes and longer tests</p>
              <p>&#8226; Higher traffic means shorter test duration</p>
              <p>&#8226; 95% significance and 80% power are industry standards</p>
              <p>&#8226; Durations are rounded to full weeks to cover day-of-week cycles</p>
            </div>

            {/* Link to full planner */}
            <FullPlannerLink onOpenChange={onOpenChange} />
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  )
}

export default TestPlanningModal
